/**
 * @module app.shared
 */

import { oneOf } from 'app/shared/lib/utilities';
import { Indexed } from 'app/shared/lib/types';

/**
 * Used to indicate that a string contains HTML and should be handled accordingly.
 *
 * For use with components such as `<ess-tooltip>` for providing an easy way to pass **HTML**
 * from a value converter.
 *
 * When generating an object that implements this interface, take care to encode any external
 * data with `escapeHtml`.
 */
export interface IHtmlValue {
    readonly html: string
}

/**
 * Basic implementation of `IHtmlValue` that also provides a `toString()` method for converting
 * to **HTML**.
 */
export class HtmlValue implements IHtmlValue {

    public static readonly Empty = new HtmlValue('');

    constructor(public readonly html: string) { }

    toString() { return this.html; }

}

/**
 * Type Guard functino for `IHtmlValue`.
 *
 * Returns true if `value` is an object that implements `IHtmlValue`.
 *
 * @param value the value to check
 */
export function isHtmlValue(value: unknown): value is IHtmlValue {
    if(!value) { return false; }
    if(typeof value === 'object') {
        return typeof (<IHtmlValue>value).html === 'string';
    }
    return false;
}

const escapeHtmlMapping: any = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&#34;',
    "'": '&#39;'
};

function replaceHtmlCharacter(char: string): string {
    return escapeHtmlMapping[char] || '';
}

/**
 * this is an internal function, use `escapeHtml` as it's generally "safer" and avoids double-encoding
 * @param text
 */
function html(text: string): string {
    return text.replace(/[&<>'"]/g, replaceHtmlCharacter);
}

/**
 * Escape special **HTML** characters.
 *
 * The following characters are encoded to **HTML** entities: `&`, `<`, `>`, `"`, `'`
 *
 * If `text` is an `IHtmlValue` instance, it is not encoded in order to prevent double-encoding
 *
 * @param text the text to escape
 * @param defaultValue the value to return if text is `undefined` or `null`
 */
export function escapeHtml(text: unknown): IHtmlValue | null;
export function escapeHtml(text: unknown, defaultValue: IHtmlValue): IHtmlValue;
export function escapeHtml(text: unknown, defaultValue?: IHtmlValue): IHtmlValue | null {
    if(text === undefined || text === null) { return defaultValue || null; }
    if(isHtmlValue(text)) { return text; }
    return new HtmlValue(html(`${text}`));
}

/**
 * Build an **HTML** value from the specified array, separated by `<br/>` line break elements.
 *
 * @param values a list of values to join with line breaks
 */
export function joinWithLineBreaks(values: any[]): IHtmlValue | null {
    if(values.length === 0) { return null; }

    const result: string[] = [];
    let first = true;
    for(const value of values) {
        if(first) {
            first = false;
        }
        else {
            result.push('<br/>');
        }
        const valueHtml = escapeHtml(value);
        if(valueHtml) {
            result.push(valueHtml.html);
        }
    }

    return new HtmlValue(result.join(''));
}

export function markText(text: string, searchRegExp: RegExp, element: string = 'mark', cssClass: string = 'ess-search'): IHtmlValue | null {
    if(!text || !searchRegExp) { return null; }

    const result: string[] = [];

    let count: number = 0;

    // sanity check (don't mark more than 100 replacements)
    let lastIndex = 0;
    while(count++ < 100) {
        const match = searchRegExp.exec(text);
        const isMatch = match !== null;
        const newIndex = isMatch ? match!.index : text.length; // move to end of text if no match
        const matchText = isMatch ? match![0] : '';

        if(lastIndex < newIndex) {
            // append everything since last match
            result.push(html(text.substring(lastIndex, newIndex)));
            if(!match) {
                break;
            }
        }
        if(matchText) {
            result.push(`<${element} class="${cssClass}">`);
            result.push(html(matchText));
            result.push(`</${element}>`);
        }
        else {
            break;
        }

        lastIndex = searchRegExp.lastIndex;
    }
    return new HtmlValue(result.join(''));
}
